import OBSWebSocket, { EventSubscription } from 'obs-websocket-js';
import { useCallback, useEffect, useRef, useState } from 'react';

export type ObsSettings = {
  ip: string;
  port: string;
  password: string;
  previewFps: number;
  previewWidth: number;
  previewQuality: number;
};

export const DEFAULT_OBS_SETTINGS: ObsSettings = {
  ip: '',
  port: '4455',
  password: '',
  previewFps: 4,
  previewWidth: 640,
  previewQuality: 50,
};

type ObsStatus = 'idle' | 'connecting' | 'connected' | 'error';

const SCENE_EVENTS = [
  'SceneListChanged',
  'SceneCreated',
  'SceneRemoved',
  'SceneNameChanged',
] as const;

export function useObs(settings: ObsSettings) {
  const obsRef = useRef<OBSWebSocket | null>(null);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  const currentSceneRef = useRef<string | null>(null);
  const manualCloseRef = useRef(false);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [status, setStatus] = useState<ObsStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [scenes, setScenes] = useState<string[]>([]);
  const [currentScene, setCurrentScene] = useState<string | null>(null);
  const [frame, setFrame] = useState<string | null>(null);

  const refreshScenes = useCallback(async () => {
    const obs = obsRef.current;
    if (!obs) return;
    try {
      const { scenes: list, currentProgramSceneName } =
        await obs.call('GetSceneList');
      // OBS lists scenes bottom-to-top; reverse to match the OBS panel order.
      setScenes(list.map((s) => s.sceneName as string).reverse());
      setCurrentScene(currentProgramSceneName);
      currentSceneRef.current = currentProgramSceneName;
    } catch {
      // transient, ignore
    }
  }, []);

  const disconnect = useCallback(async () => {
    manualCloseRef.current = true;
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    const obs = obsRef.current;
    obsRef.current = null;
    setStatus('idle');
    setError(null);
    setScenes([]);
    setCurrentScene(null);
    setFrame(null);
    currentSceneRef.current = null;
    if (obs) {
      try {
        await obs.disconnect();
      } catch {
        // ignore
      }
    }
  }, []);

  const connect = useCallback(async () => {
    const current = settingsRef.current;
    if (!current.ip) {
      setError('Set the OBS host IP first.');
      setStatus('error');
      return;
    }

    // Tear down any previous instance.
    if (obsRef.current) {
      try {
        await obsRef.current.disconnect();
      } catch {
        // ignore
      }
    }

    manualCloseRef.current = false;
    setError(null);
    setStatus('connecting');

    const obs = new OBSWebSocket();
    obsRef.current = obs;

    obs.on('CurrentProgramSceneChanged', ({ sceneName }) => {
      setCurrentScene(sceneName);
      currentSceneRef.current = sceneName;
    });

    for (const evt of SCENE_EVENTS) {
      obs.on(evt as 'SceneListChanged', () => {
        void refreshScenes();
      });
    }

    obs.on('ConnectionClosed', () => {
      if (obsRef.current !== obs) return; // superseded by a newer instance
      setStatus('idle');
      setFrame(null);
      if (!manualCloseRef.current) {
        setError('Connection lost. Reconnecting…');
        reconnectTimer.current = setTimeout(() => {
          if (obsRef.current === obs) void connect();
        }, 2000);
      }
    });

    try {
      const url = `ws://${current.ip}:${current.port || '4455'}`;
      await obs.connect(url, current.password || undefined, {
        eventSubscriptions:
          EventSubscription.Scenes | EventSubscription.General,
      });
      if (obsRef.current !== obs) return;
      setStatus('connected');
      setError(null);
      await refreshScenes();
    } catch (err) {
      if (obsRef.current !== obs) return;
      setStatus('error');
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to connect to OBS WebSocket.',
      );
    }
  }, [refreshScenes]);

  const switchScene = useCallback(async (name: string) => {
    const obs = obsRef.current;
    if (!obs) return;
    // Optimistic update; OBS confirms via CurrentProgramSceneChanged.
    setCurrentScene(name);
    currentSceneRef.current = name;
    try {
      await obs.call('SetCurrentProgramScene', { sceneName: name });
    } catch {
      // ignore; event will reconcile
    }
  }, []);

  // Preview loop: poll a screenshot of the current program scene.
  useEffect(() => {
    if (status !== 'connected') return;
    const fps = Math.min(Math.max(settings.previewFps, 0.5), 15);
    const interval = Math.max(50, Math.round(1000 / fps));
    let cancelled = false;

    const tick = async () => {
      const obs = obsRef.current;
      const scene = currentSceneRef.current;
      if (!obs || !scene) return;
      try {
        const { imageData } = await obs.call('GetSourceScreenshot', {
          sourceName: scene,
          imageFormat: 'jpg',
          imageWidth: settingsRef.current.previewWidth,
          imageCompressionQuality: settingsRef.current.previewQuality,
        });
        if (!cancelled) setFrame(imageData);
      } catch {
        // scene with no video output, ignore
      }
    };

    const timer = setInterval(tick, interval);
    void tick();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    status,
    settings.previewFps,
    settings.previewWidth,
    settings.previewQuality,
  ]);

  // Clean up on unmount.
  useEffect(() => {
    return () => {
      manualCloseRef.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      obsRef.current?.disconnect().catch(() => {});
      obsRef.current = null;
    };
  }, []);

  return {
    status,
    error,
    scenes,
    currentScene,
    frame,
    connect,
    disconnect,
    switchScene,
  };
}
